import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { ModalController, NavParams, Platform } from "ionic-angular";
import moment from "moment";
import { Observable, Subscription } from "rxjs";
import "rxjs/add/operator/map";
import { forkJoin } from "rxjs/observable/forkJoin";
import { CertificationService } from "../../../certification/services/certification-service";
import PatientBiometricsUpdate from "../../../models/idg/patient-biometrics-update";
import PatientChart from "../../../models/idg/patient-chart";
import Measure from "../../../models/patient-chart/biometrics/measure";
import { IDGDataService } from "../../../providers/idg-data-service/idg-data-service";
import { BiometricsService } from "../../../providers/patient-charts/biometrics.service";
import { ReferralsServiceProvider } from "../../../providers/patient-charts/referrals-service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import { DeclineDashboardPopover } from "../decline-dashboard-popover/decline-dashboard-popover";

@Component({
  selector: "idg-dare",
  templateUrl: "idg-dare.html",
})
export class IDGDareComponent implements OnInit, OnDestroy {
  @Input() patientData: any[];
  @Input() meetingId: string;
  @Input() meetingStatus: string;
  @Input() userMeetingRoles: any[];
  @Input() actionsTaken: any[] = [];
  @Input() hasWrittenCti = null;
  @Output() actionClick: EventEmitter<any> = new EventEmitter();
  @Output() selectPatient: EventEmitter<any> = new EventEmitter();
  @Output() toggleDareSection: EventEmitter<any> = new EventEmitter();
  @Output() recertifyClicked: EventEmitter<any> = new EventEmitter<any>();

  assessmentUrl: string;
  dataUrl: string;
  chartId: string;
  patChartId: any;
  patientNeedsCertification: boolean = false;
  showPatientBiometrics: boolean = false;
  showAnalytics: boolean = false;
  showIdgTempChanges: boolean = false;
  patientBiometricsSubscription: Subscription;
  analyticsSubscription: Subscription;
  showIdgTempChangesSubscription: Subscription;
  patientCertificationToggle: boolean = false;
  patientCertificationV2Toggle: boolean = false;

  public dareData: any[] = [
    {
      name: "Discharged",
      patients: [],
    },
    {
      name: "Admitted",
      patients: [],
    },
    {
      name: "Recertification",
      patients: [],
    },
    {
      name: "Existing",
      patients: [],
    },
  ];

  // Summary status data
  summaryStatusData: any[] = [];
  summaryStatusLoading: boolean = false;
  isIdgSummaryServiceAvailable: boolean = false;

  public patientKeys: any[] = [
    // "qbosId",
    "patientFullName", // set in dareSort
    "gender",
    "primaryCaregiverPhone",
    "primaryDiagnosis",
    "careLocationCity",
    "dnrstatus",
    "levelOfCare",
    "firstLanguage",
    "ethnicity",
    "patientStatus",
  ];

  public patientKeysIfShowIdgTempChanges: any[] = [
    "genderAndAge",
    "patientStatus",
    "benefitPeriod",
    "admitDate",
    "recertificationDate",
    "lastF2FDatetime",
    "lastIdgDatetime",
    "primaryCaregiverName",
    "primaryCaregiverPhone",
    "primaryDiagnosis",
    "dnrstatus",
    "levelOfCare",
    "careLocationType",
    "attendingPhysician",
  ];

  // patient biometrics keys - always added last - (if showPatientBiometrics toggle is OFF)
  public biometricsKeys: any[] = [
    "initialWeightKgs",
    "currentWeightKgs",
    "initialMMSEScore",
    "currentMMSEScore",
    "initialPPSScore",
    "currentPPSScore",
    "initialKarnofskyScore",
    "currentKarnofskyScore",
    "initialNYHAScore",
    "currentNYHAScore",
    "initialMUACScore",
    "currentMUACScore",
    "initialFASTScore",
    "currentFASTScore",
  ];

  // sub-groups - needed to identify how to display  (see html file)
  public dateKeys: any[] = [
    "admitDate",
    "recertificationDate",
    // "dischargeDate",
    //"patientBirthdate"
  ];

  public dateKeysWithDaysSince: any[] = ["lastF2FDatetime"];

  public dateKeysWithLocalDaysSince: any[] = ["lastIdgDatetime"];

  public simpleTextKeys: any[] = [
    "genderAndAge",
    "patientAge",
    "patientStatus",
    "benefitPeriod",
    "primaryCaregiverName",
    "primaryCaregiverPhone",
    "primaryDiagnosis",
    "dnrstatus",
    "levelOfCare",
    "careLocationType",
    "attendingPhysician",
  ];

  idgUserRoles: string;

  constructor(
    public plt: Platform,
    public stateData: StateManagementService,
    public idgSvc: IDGDataService,
    private certificationSvc: CertificationService,
    public referralService: ReferralsServiceProvider,
    private cdr: ChangeDetectorRef,
    public navParams: NavParams,
    public biometricsService: BiometricsService,
    private modalCtrl: ModalController
  ) {
    this.getIDGUserRoles().subscribe(
      (data) => {
        this.idgUserRoles = data;
      },
      (err) => {
        console.log(err);
      }
    );
  }

  ngOnInit(): void {
    this.analyticsSubscription = this.stateData.showAnalytics.subscribe((x) => {
      this.showAnalytics = x;
    });

    this.showIdgTempChangesSubscription =
      this.stateData.showIdgTempChanges.subscribe((x) => {
        this.showIdgTempChanges = x;
      });
    if (this.showIdgTempChanges)
      this.patientKeys = this.patientKeysIfShowIdgTempChanges;

    this.patientBiometricsSubscription =
      this.stateData.showPatientBiometrics.subscribe((x) => {
        this.showPatientBiometrics = x;
      });
    if (!this.showPatientBiometrics) {
      // if toggle is OFF show biometricsKeys; if ON hide them and show "Patient Biometrics" graph
      this.patientKeys = [...this.patientKeys, ...this.biometricsKeys];
      this.simpleTextKeys = [...this.simpleTextKeys, ...this.biometricsKeys];
    }

    let ctiV1Toggle = this.stateData.showCtiForm.subscribe((x) => {
      this.patientCertificationToggle = x;
    });

    let ctiV2Toggle = this.stateData.ctiV2.subscribe((x) => {
      this.patientCertificationV2Toggle = x;
    });

    // Set up IDG summary service availability
    this.stateData.showAiIdgSummaryComponents.subscribe((value) => {
      this.isIdgSummaryServiceAvailable = value;
    });

    // Load summary status for all patients only if feature toggle is enabled
    if (this.stateData.showAiIdgSummaryComponents.value) {
      this.loadSummaryStatus();
    }
  }

  ngOnDestroy() {
    if (this.patientBiometricsSubscription) {
      this.patientBiometricsSubscription.unsubscribe();
    }
    if (this.analyticsSubscription) {
      this.analyticsSubscription.unsubscribe();
    }
    if (this.showIdgTempChangesSubscription) {
      this.showIdgTempChangesSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes) {
    if (changes.patientData) {
      this.dareSort(changes.patientData.currentValue);
      // Refresh summary status when patient data changes
      this.loadSummaryStatus();
    }
  }

  checkAction(patientId, buttonString) {
    if (buttonString === "RC" && !this.hasNoWrittenCti(patientId)) {
      return "action-completed";
    }

    return this.actionsTaken.find((action) => {
      return action.patient.id == patientId && action.action === buttonString;
    })
      ? "action-completed"
      : "";
  }

  checkActionForCertify(patientId, buttonString) {
    if (buttonString === "RC" && !this.checkNoWrittenCti(patientId)) {
      return "action-completed";
    }

    return this.actionsTaken.find((action) => {
      return action.patient.id == patientId && action.action === buttonString;
    })
      ? "action-completed"
      : "";
  }

  dareSort(patients) {
    if (patients) {
      patients.forEach((patient) => {
        if (patient.patientChart) {
          patient.patientChart.id = patient.id;
          patient.patientChart.dareStatus = patient.dareStatus;
          patient.patientChart.patientFullName = patient.displayName;
          patient.patientChart.gender = patient.gender;
        } else {
          patient.patientChart = {};
          patient.patientChart.dareStatus = patient.dareStatus;
          patient.patientChart.patientFullName = patient.displayName;
          patient.patientChart.id = patient.id;
          patient.patientChart.qbosId = undefined;
          patient.patientChart.error = "Error getting patient chart";
          patient.patientChart.gender = patient.gender;
        }

        this.pushDarePatient(patient);
      });
    }
  }

  pushDarePatient(patient) {
    let darePatient = {
      ...patient,
      name: patient.displayName,
    };

    if (this.showIdgTempChanges && patient.idgmeetingVersion > 1) {
      // New DARE Categorization
      if (patient.dareStatusDischarge) {
        this.dareData[0].patients.push(darePatient);
      }
      if (patient.dareStatusAdmitted) {
        this.dareData[1].patients.push(darePatient);
      }
      if (patient.dareStatusRecertification) {
        this.dareData[2].patients.push(darePatient);
      }
      if (patient.dareStatusExisting) {
        this.dareData[3].patients.push(darePatient);
      }
    } else {
      // Old DARE Categorization
      if (patient.dareStatus === "Discharged") {
        this.dareData[0].patients.push(darePatient);
      } else if (patient.dareStatus === "Admitted") {
        this.dareData[1].patients.push(darePatient);
      } else if (patient.dareStatus === "Recertification") {
        this.dareData[2].patients.push(darePatient);
      } else if (patient.dareStatus === "Existing") {
        this.dareData[3].patients.push(darePatient);
      }
    }
  }

  getIDGUserRoles(): any {
    return new Observable((observer) => {
      try {
        let meetingId = this.navParams.get("meetingId");
        this.idgSvc.getMeetingUserInfo(meetingId).subscribe(
          (data) => {
            observer.next(data.roles);
            observer.complete();
          },
          (err) => {
            observer.error(err);
            observer.complete();
          }
        );
      } catch (error) {
        console.log("Error while retrieving user role", error);
      }
    });
  }

  userIsMD(): boolean {
    return (
      (this.idgUserRoles && this.idgUserRoles.includes("MD")) ||
      (this.userMeetingRoles && this.userMeetingRoles.includes("MD"))
    );
  }

  toggleSection(i) {
    this.toggleDareSection.emit(this.dareData[i]);
    this.dareData.forEach((section) => {
      if (this.dareData.indexOf(section) === i) {
        section.open = !section.open;
        if (!section.open) {
          section.patients.forEach((childItem) => {
            childItem.open = false;
          });
        }
      } else {
        // this.selectPatient.emit(null);
        // close all open patients in other tabs
        section.patients.forEach((childItem) => {
          childItem.open = false;
        });
        section.open = false;
      }
      // if section is closed, the patient is deselected
      this.selectPatient.emit(null);
    });
  }

  toggleItem(sectionIndex, patientsIndex) {
    this.dareData[sectionIndex].patients.forEach((childItem) => {
      if (!childItem.patientChart.qbosId) {
        childItem.patientChart.qbosId = childItem.qbosId;
      }
      if (
        this.dareData[sectionIndex].patients.indexOf(childItem) ===
        patientsIndex
      ) {
        childItem.open = !childItem.open;
        if (childItem.open) {
          const openedPatient =
            this.dareData[sectionIndex].patients[patientsIndex];
          this.selectPatient.emit(openedPatient);
          if (this.showIdgTempChanges) {
            this.certificationSvc
              .GetCurrentBenefitPeriod(
                openedPatient.qbosId,
                moment().local(true).format("YYYY-MM-DD")
              )
              .pipe()
              .take(1)
              .subscribe(
                (benefitPeriod: any) => {
                  if (benefitPeriod) {
                    openedPatient.patientChart.benefitPeriod = benefitPeriod[
                      "benefitPeriod"
                    ]
                      ? benefitPeriod["benefitPeriod"]
                      : "";
                  }
                },
                (error) => {
                  console.error(error);
                }
              );
            this.certificationSvc
              .getCertificationListV2(openedPatient.qbosId)
              .pipe()
              .take(1)
              .subscribe(
                (certifications: any) => {
                  if (
                    certifications &&
                    certifications.content &&
                    certifications.content.length
                  ) {
                    const latestCertification = certifications.content.reduce(
                      (latest, current) => {
                        const latestDate = latest.certifiedDateTime;
                        const currentDate = current.certifiedDateTime;
                        if (!currentDate) return latest;
                        if (!latestDate) return current;
                        return moment(currentDate).isAfter(moment(latestDate))
                          ? current
                          : latest;
                      },
                      certifications.content[0]
                    );
                    openedPatient.hasCompleteWrittenCTI =
                      certifications.content.some(
                        (cert) =>
                          cert.type === "WRITTEN_PATIENT_CHART_V2" &&
                          cert.status === "YES"
                      );
                    openedPatient.patientChart.recertificationDate =
                      latestCertification.certifiedDateTime;
                  }
                },
                (error) => {
                  console.error(error);
                }
              );
            this.referralService
              .getPatientChartBrief(openedPatient.qbosId, true, null)
              .subscribe((briefInfo) => {
                if (briefInfo) {
                  openedPatient.patientChart.attendingPhysician =
                    briefInfo.attendingPhysicianName;
                  openedPatient.patientChart.primaryCaregiverPhone =
                    briefInfo.primaryCareGiverPhoneNumber;
                  openedPatient.patientChart.primaryCaregiverName =
                    briefInfo.primaryCareGiverName;
                }
              });
          }

          this.patientNeedsCertification = false;
          if (this.patientCertificationV2Toggle) {
            // CTI_v2 - If patient belongs in ADMITTED or RECERTIFICATION, check for certification need
            if (sectionIndex === 1 || sectionIndex === 2) {
              let dareStatus =
                sectionIndex === 1 ? "ADMITTED" : "RECERTIFICATION";
              this.certificationSvc
                .getWrittenRequiredStatusV2(openedPatient.qbosId, dareStatus)
                .subscribe((data) => {
                  if (data) {
                    this.patientNeedsCertification = data.required;
                    this.cdr.detectChanges();
                  }
                });
            }
          }
          if (this.meetingStatus != "CP") {
            // fetch KPI data from biometrics service
            this.fetchMeasures(childItem.patientChart);
          }
          //do some conversion to imperial values
          this.convertToImperialValues(childItem.patientChart);
        } else {
          this.selectPatient.emit(null);
        }
      } else {
        childItem.open = false;
      }
    });
  }

  convertValues(input, conversionFactor, endUnit): string {
    //initial load - "42.3, 43.5" (in metric)
    // after convert - "99.8 lbs, 98.7 lbs, 42.3 kgs, 43.5kgs"
    let startUnit = "kgs";
    if (endUnit === "in") {
      startUnit = "cm";
    }
    if (input) {
      let values = input.split(", ");
      values.forEach((value) => {
        if (!value.includes(endUnit) || !value.includes(startUnit)) {
          let converted = (value * conversionFactor).toFixed(1);
          if (converted === "NaN") {
            values[values.indexOf(value)] = value;
          } else {
            values[values.indexOf(value)] =
              value + " " + startUnit + " / " + converted + " " + endUnit;
          }
        }
      });
      let res = values.join(", ");
      return res === "NaN" ? "---" : res;
    }
  }

  convertMUACInToCm(input, conversionFactor, startUnit, endUnit) {
    if (input) {
      let values = input.split(",").map((v) => v.trim());
      let labels = ["(L)", "(R)"];
      values.forEach((value, index) => {
        let label = labels[index] || "";
        if (value === "-") {
          values[index] = label + " -";
        } else if (!value.includes(endUnit) || !value.includes(startUnit)) {
          let converted = (value / conversionFactor).toFixed(1);
          if (converted === "NaN") {
            values[index] = label + " " + value;
          } else {
            values[index] =
              label +
              " " +
              value +
              " " +
              startUnit +
              " / " +
              converted +
              " " +
              endUnit;
          }
        }
      });
      let res = values.join(", ");
      return res === "NaN" ? "---" : res;
    }
  }

  takeAction(patChartId, buttonString) {
    this.actionClick.emit({ patChartId, buttonString });
  }

  emitRecertifyClicked(patient) {
    this.recertifyClicked.emit(patient);
  }

  protected get showCertifyButton() {
    if (this.patientCertificationV2Toggle) {
      return this.stateData.ctiV2;
    }
    return this.stateData.showCtiForm;
  }

  protected trackPatientFunc(index, patient) {
    if (!patient) return null;
    return patient.id;
  }

  protected checkPatientNeedsCertification(patientId) {
    if (this.patientCertificationV2Toggle) {
      let actionTaken = this.actionsTaken.find((action) => {
        return action.patient.id == patientId && action.action === "RC";
      });
      return actionTaken != undefined
        ? actionTaken
        : this.patientNeedsCertification;
    } else {
      return this.patientNeedsCertification;
    }
  }

  protected checkNoWrittenCti(patientId) {
    if (this.patientCertificationV2Toggle) {
      return this.patientNeedsCertification;
    } else {
      return this.hasNoWrittenCti(patientId);
    }
  }

  protected hasNoWrittenCti(patientId) {
    return !this.hasWrittenCti || !this.hasWrittenCti[patientId];
  }

  convertToImperialValues(patientChart: PatientChart) {
    //do some conversion to imperial values
    if (
      patientChart.initialMUACScore &&
      !patientChart.initialMUACScore.includes("(L)")
    ) {
      patientChart.initialMUACScore = this.convertMUACInToCm(
        patientChart.initialMUACScore,
        0.3937,
        "in",
        "cm"
      );
    }
    if (
      patientChart.currentMUACScore &&
      !patientChart.currentMUACScore.includes("(L)")
    ) {
      patientChart.currentMUACScore = this.convertMUACInToCm(
        patientChart.currentMUACScore,
        0.3937,
        "in",
        "cm"
      );
    }
    if (
      patientChart.initialWeightKgs &&
      !patientChart.initialWeightKgs.includes("lbs")
    ) {
      patientChart.initialWeightKgs =
        patientChart.initialWeightKgs +
        " kgs / " +
        patientChart.initialWeightLbs +
        " lbs";
    }
    if (
      patientChart.currentWeightKgs &&
      !patientChart.currentWeightKgs.includes("lbs")
    ) {
      patientChart.currentWeightKgs =
        patientChart.currentWeightKgs +
        " kgs / " +
        patientChart.currentWeightLbs +
        " lbs";
    }
  }

  fetchMeasures(patientChart: PatientChart) {
    let kilograms = this.biometricsService.getReadings(
      patientChart.qbosId,
      "kilograms"
    );
    let pounds = this.biometricsService.getReadings(
      patientChart.qbosId,
      "pounds"
    );
    let mmseScore = this.biometricsService.getReadings(
      patientChart.qbosId,
      "mmseScore"
    );
    let ppsScore = this.biometricsService.getReadings(
      patientChart.qbosId,
      "assessorScore"
    );
    let karnofskyScore = this.biometricsService.getReadings(
      patientChart.qbosId,
      "karnofskyValue"
    );
    let nyhaScore = this.biometricsService.getReadings(
      patientChart.qbosId,
      "nyhaClass"
    );
    let lMuacScore = this.biometricsService.getReadings(
      patientChart.qbosId,
      "leftArmInches"
    );
    let rMuacScore = this.biometricsService.getReadings(
      patientChart.qbosId,
      "rightArmInches"
    );
    let fastScore = this.biometricsService.getReadings(
      patientChart.qbosId,
      "fastValue"
    );
    let patientBiometricsUpdate: PatientBiometricsUpdate =
      new PatientBiometricsUpdate();

    forkJoin([
      kilograms,
      pounds,
      mmseScore,
      ppsScore,
      karnofskyScore,
      nyhaScore,
      lMuacScore,
      rMuacScore,
      fastScore,
    ]).subscribe(
      ([kgs, lbs, mmse, pps, karnofsky, nyha, lMuac, rMuac, fast]: [
        Measure,
        Measure,
        Measure,
        Measure,
        Measure,
        Measure,
        Measure,
        Measure,
        Measure
      ]) => {
        if (kgs.readings) {
          if (
            patientChart.initialWeightKgs == null ||
            patientChart.initialWeightKgs === ""
          ) {
            console.log("Override weightKgs with data from Timestream");
            patientChart.initialWeightKgs = kgs.readings[0].value + "";
            patientBiometricsUpdate.initialWeightKgs =
              kgs.readings[0].value + "";
            patientBiometricsUpdate.initialWeightDatetime =
              kgs.readings[0].time + "";
          }
          patientChart.currentWeightKgs =
            kgs.readings[kgs.readings.length - 1].value + "";
          patientBiometricsUpdate.currentWeightKgs =
            kgs.readings[kgs.readings.length - 1].value + "";
          patientBiometricsUpdate.currentWeightDatetime =
            kgs.readings[kgs.readings.length - 1].time + "";
        } else {
          console.log("No data received for kilograms");
        }

        if (lbs.readings) {
          console.log("Receiving data for pounds");
          if (
            patientChart.initialWeightLbs == null ||
            patientChart.initialWeightLbs === ""
          ) {
            patientChart.initialWeightLbs = lbs.readings[0].value + "";
            patientBiometricsUpdate.initialWeightLbs =
              lbs.readings[0].value + "";
            patientBiometricsUpdate.initialWeightDatetime =
              lbs.readings[0].time + "";
          }
          patientChart.currentWeightLbs =
            lbs.readings[lbs.readings.length - 1].value + "";
          patientBiometricsUpdate.currentWeightLbs =
            lbs.readings[lbs.readings.length - 1].value + "";
          patientBiometricsUpdate.currentWeightDatetime =
            lbs.readings[lbs.readings.length - 1].time + "";
        } else {
          console.log("No data received for pounds");
        }

        if (mmse.readings) {
          console.log("Receiving data for mmseScore");
          if (
            patientChart.initialMMSEScore == null ||
            patientChart.initialMMSEScore === ""
          ) {
            patientChart.initialMMSEScore = mmse.readings[0].value + "";
            patientBiometricsUpdate.initialMmseScore =
              mmse.readings[0].value + "";
            patientBiometricsUpdate.initialMmseScoreDatetime =
              mmse.readings[0].time + "";
          }
          patientChart["currentMMSEScore"] =
            mmse.readings[mmse.readings.length - 1].value + "";
          patientBiometricsUpdate.currentMmseScore =
            mmse.readings[mmse.readings.length - 1].value + "";
          patientBiometricsUpdate.currentMmseScoreDatetime =
            mmse.readings[mmse.readings.length - 1].time + "";
        } else {
          console.log("No data received for mmseScore");
        }

        if (pps.readings) {
          console.log("Receiving data for ppsScore");
          if (
            patientChart.initialPPSScore == null ||
            patientChart.initialPPSScore === ""
          ) {
            patientChart.initialPPSScore = pps.readings[0].value + "";
            patientBiometricsUpdate.initialPpsScore =
              pps.readings[0].value + "";
            patientBiometricsUpdate.initialPpsScoreDatetime =
              pps.readings[0].time + "";
          }
          patientChart.currentPPSScore =
            pps.readings[pps.readings.length - 1].value + "";
          patientBiometricsUpdate.currentPpsScore =
            pps.readings[pps.readings.length - 1].value + "";
          patientBiometricsUpdate.currentPpsScoreDatetime =
            pps.readings[pps.readings.length - 1].time + "";
        } else {
          console.log("No data received for ppsScore");
        }

        if (karnofsky.readings) {
          console.log("Receiving data for karnofskyScore");
          if (
            patientChart.initialKarnofskyScore == null ||
            patientChart.initialKarnofskyScore === ""
          ) {
            patientChart.initialKarnofskyScore =
              karnofsky.readings[0].value + "";
            patientBiometricsUpdate.initialKarnofskyScore =
              karnofsky.readings[0].value + "";
            patientBiometricsUpdate.initialKarnofskyScoreDatetime =
              karnofsky.readings[0].time + "";
          }
          patientChart.currentKarnofskyScore =
            karnofsky.readings[karnofsky.readings.length - 1].value + "";
          patientBiometricsUpdate.currentKarnofskyScore =
            karnofsky.readings[karnofsky.readings.length - 1].value + "";
          patientBiometricsUpdate.currentKarnofskyScoreDatetime =
            karnofsky.readings[karnofsky.readings.length - 1].time + "";
        } else {
          console.log("No data received for karnofskyScore");
        }

        if (nyha.readings) {
          console.log("Receiving data for nyhaScore");
          if (
            patientChart.initialNYHAScore == null ||
            patientChart.initialNYHAScore === ""
          ) {
            patientChart.initialNYHAScore = nyha.readings[0].value + "";
            patientBiometricsUpdate.initialNyhaScore =
              nyha.readings[0].value + "";
            patientBiometricsUpdate.initialNyhaScoreDatetime =
              nyha.readings[0].time + "";
          }
          patientChart.currentNYHAScore =
            nyha.readings[nyha.readings.length - 1].value + "";
          patientBiometricsUpdate.currentNyhaScore =
            nyha.readings[nyha.readings.length - 1].value + "";
          patientBiometricsUpdate.currentNyhaScoreDatetime =
            nyha.readings[nyha.readings.length - 1].time + "";
        } else {
          console.log("No data received for nyhaScore");
        }

        if (lMuac.readings || rMuac.readings) {
          console.log("Receiving data for muacScore");
          let initialLeft = lMuac.readings ? lMuac.readings[0].value + "" : "-";
          let initialRight = rMuac.readings
            ? rMuac.readings[0].value + ""
            : "-";
          let currentLeft = lMuac.readings
            ? lMuac.readings[lMuac.readings.length - 1].value + ""
            : "-";
          let currentRight = rMuac.readings
            ? rMuac.readings[rMuac.readings.length - 1].value + ""
            : "-";

          if (
            patientChart.initialMUACScore == null ||
            patientChart.initialMUACScore === ""
          ) {
            console.log("Override init MUAC Score in cm from Timestream");
            patientChart.initialMUACScore = initialLeft + ", " + initialRight;
            patientBiometricsUpdate.initialMuacScore =
              initialLeft + ", " + initialRight;
            patientBiometricsUpdate.initialMuacScoreDatetime =
              (lMuac.readings
                ? lMuac.readings[0].time
                : rMuac.readings[0].time) + "";
          }
          patientChart.currentMUACScore = currentLeft + ", " + currentRight;
          patientBiometricsUpdate.currentMuacScore =
            currentLeft + ", " + currentRight;
          patientBiometricsUpdate.currentMuacScoreDatetime =
            (lMuac.readings
              ? lMuac.readings[lMuac.readings.length - 1].time
              : rMuac.readings[rMuac.readings.length - 1].time) + "";
        } else {
          console.log("No data received for muacScore");
        }

        if (fast.readings) {
          console.log("Receiving data for fastScore");
          if (
            patientChart.initialFASTScore == null ||
            patientChart.initialFASTScore === ""
          ) {
            patientChart.initialFASTScore = fast.readings[0].value + "";
            patientBiometricsUpdate.initialFastScore =
              fast.readings[0].value + "";
            patientBiometricsUpdate.initialFastScoreDatetime =
              fast.readings[0].time + "";
          }
          patientChart.currentFASTScore =
            fast.readings[fast.readings.length - 1].value + "";
          patientBiometricsUpdate.currentFastScore =
            fast.readings[fast.readings.length - 1].value + "";
          patientBiometricsUpdate.currentFastScoreDatetime =
            fast.readings[fast.readings.length - 1].time + "";
        } else {
          console.log("No data received for fastScore");
        }

        this.convertToImperialValues(patientChart);
        console.log("PatientBiometricsUpdate: ", patientBiometricsUpdate);
        // call endpoint to update patient biometrics in IDG
        this.biometricsService
          .updatePatientBiometrics(
            patientChart.qbosId,
            this.meetingId,
            patientBiometricsUpdate
          )
          .subscribe(
            (response) => {
              console.log(
                "Successfully updated patient biometrics",
                response.patientChart
              );
            },
            (err) => {
              console.log("Failed to update patient biometrics", err);
            }
          );
      },
      (err) => {
        console.log("Failed to fetch patient biometrics", err);
      }
    );
  }

  presentAlertsPopover(patientChartId) {
    console.log(`patientChartId ${patientChartId}`);
    let modal = this.modalCtrl.create(
      "BiometricsPage",
      { patientChartId: patientChartId },
      { cssClass: "biometrics-modal" }
    );
    modal.present();
  }

  presentPatientDeclineDashboard() {
    const patientChartId = window.sessionStorage.getItem("patientChartId");
    let popover = this.modalCtrl.create(
      DeclineDashboardPopover,
      {
        patientChartId: patientChartId,
      },
      { cssClass: "decline-dashboard-modal" }
    );
    popover.present({});
  }

  isDaysSinceMoreThan14(givenDatetime) {
    if (givenDatetime) {
      let givenDatetimeInLocal = moment.utc(givenDatetime, "YYYY/MM/DD HH:mm:ss").local();
      let givenDate = moment(givenDatetimeInLocal).toDate();
      const timeSinceGivenDate = Math.abs(
        Date.now() - new Date(givenDate).getTime()
      );
      let daysSince = Math.floor(
        timeSinceGivenDate / (1000 * 3600 * 24)
      ).toString();
      return parseInt(daysSince) > 14;
    }
    return false;
  }

  isActionRequired(patient) {
    switch (patient.patientChart.dareStatus) {
      case "Discharged":
        return false;
      case "Admitted":
      case "Recertification":
      case "Existing":
        return (
          this.checkAction(patient.patientChart.id, "AP") !==
            "action-completed" &&
          this.checkAction(patient.patientChart.id, "AM") !== "action-completed"
        );
      default:
        return false;
    }
  }

  /**
   * Fetches the summary status for all patients in the meeting
   */
  loadSummaryStatus(): void {
    if (!this.meetingId || !this.isIdgSummaryServiceAvailable) {
      return;
    }

    this.summaryStatusLoading = true;

    this.idgSvc.getSummaryStatus(Number(this.meetingId)).subscribe(
      (response: any[]) => {
        this.summaryStatusData = response;
        this.summaryStatusLoading = false;
      },
      (error) => {
        console.error("Error fetching summary status:", error);
        this.summaryStatusData = [];
        this.summaryStatusLoading = false;
      }
    );
  }

  /**
   * CO-2962 — Locate a patient's *visible* summary status row, ignoring any
   * user-deleted ("removed") rows.
   *
   * A clinician-initiated "Delete Prep" soft-deletes the idg_meeting_summary
   * row; the read API (getAllSummaryStatuses) still returns that row as status
   * "removed", and a subsequent "Refresh Prep" creates a NEW live row for the
   * same patient — so the API can return BOTH a "removed" row and a live row
   * for one patientChartId. Per Rob Jackson's CO-2962 decision, a user-deleted
   * prep must read as "no summary" (no status indicator, no count), while a
   * system-side FAILED summary (CO-2312/CO-2416) keeps showing. Filtering out
   * "removed" here does both: a delete-only patient resolves to no row, and a
   * delete-then-refresh patient resolves to the live row. There is at most one
   * non-deleted row per patient.
   */
  private findVisibleSummaryStatus(patient: any): any {
    if (!this.summaryStatusData || !patient?.patientChart?.qbosId) {
      return undefined;
    }
    return this.summaryStatusData.find(
      (status) =>
        status &&
        status.patientChartId === patient.patientChart.qbosId &&
        String(status.status).toUpperCase() !== "REMOVED"
    );
  }

  /**
   * Gets the summary status text for a specific patient
   * @param patient - The patient object
   * @returns The summary status text or empty string
   */
  getSummaryText(patient: any): string {
    const patientStatus = this.findVisibleSummaryStatus(patient);

    if (!patientStatus || !patientStatus.status) {
      return "";
    }

    // Backend persists/returns UPPERCASE status values ("COMPLETED"/"FAILED"/
    // "PENDING"). Normalize to upper case so a case mismatch can never silently
    // blank a status (CO-2312). User-deleted rows ("removed") are filtered out
    // by findVisibleSummaryStatus (CO-2962) and never reach this switch, so a
    // deleted prep renders nothing in the DARE sidebar.
    const normalizedStatus = String(patientStatus.status).toUpperCase();

    switch (normalizedStatus) {
      // COMPLETED is the backend value; "READY" kept for backward compatibility.
      case "COMPLETED":
      case "READY":
        return "summaryReady";
      // FAILED is the backend value; "ERROR" is the enum name, kept defensively.
      case "FAILED":
      case "ERROR":
        return "summaryFailed";
      case "PENDING":
      default:
        return "";
    }
  }

  /**
   * CO-2416 — Returns the per-patient `errorMessage` string for a FAILED
   * summary.
   *
   * The consumer Lambda (CO-2416 BE / idg-v2) populates `errorMessage` on
   * the read-API row when status === "FAILED" (e.g. "report not
   * generated"). Returns "" when no row, no errorMessage, or status !=
   * FAILED so the template falls back to the generic "Summary Unavailable"
   * label.
   *
   * @param patient - The patient object
   * @returns The errorMessage string for a failed summary, or "" if
   *          unavailable
   */
  getSummaryReason(patient: any): string {
    const patientStatus = this.findVisibleSummaryStatus(patient);

    if (!patientStatus || !patientStatus.status) {
      return "";
    }

    const normalizedStatus = String(patientStatus.status).toUpperCase();
    if (normalizedStatus !== "FAILED" && normalizedStatus !== "ERROR") {
      return "";
    }

    // Defensive: trim and treat null/undefined/blank as no errorMessage.
    const errorMessage = patientStatus.errorMessage;
    return typeof errorMessage === "string" ? errorMessage.trim() : "";
  }

  /**
   * CO-2416 — Aggregate per-meeting summary counts for the header banner.
   *
   * Allows clinicians to see at a glance "23 summarized, 1 failed" instead of
   * having to scroll the DARE list and infer state per patient. PENDING and
   * REMOVED rows count toward total but not toward completed or failed.
   *
   * @returns { completed, failed, total } — zeros when no data is loaded.
   */
  getSummaryAggregate(): {
    completed: number;
    failed: number;
    total: number;
  } {
    const empty = { completed: 0, failed: 0, total: 0 };
    if (!this.summaryStatusData || !this.summaryStatusData.length) {
      return empty;
    }

    let completed = 0;
    let failed = 0;
    let removed = 0;
    for (const row of this.summaryStatusData) {
      if (!row || !row.status) continue;
      const s = String(row.status).toUpperCase();
      if (s === "COMPLETED" || s === "READY") {
        completed++;
      } else if (s === "FAILED" || s === "ERROR") {
        failed++;
      } else if (s === "REMOVED") {
        removed++;
      }
    }

    return {
      completed,
      failed,
      // CO-2962: user-deleted preps ("removed") are treated as if no summary
      // exists — excluded from the banner total, not just from the
      // completed/failed tallies. Other rows (pending / null-status
      // placeholders) still count toward the patient total.
      total: this.summaryStatusData.length - removed,
    };
  }
}



// WEBPACK FOOTER //
// ./src/navigation/components/idg-dare/idg-dare.ts